import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiTag, FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  DesktopOnly,
  MobileOnly,
  MobileOrderList,
  MobileOrderCard,
  MobileOrderHeaderMain,
  MobileOrderHeaderAside,
  MobileOrderId,
  MobileOrderMeta,
  MobileBadgeRow,
  MobileCardBody,
  MobileCardActions,
  mobileCardStyles,
} from '../../styles/orderMobileLayout';

const Container = styled.div`
  padding: 32px;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${props => props.primary ? '#5efba9' : 'white'};
  color: ${props => props.primary ? '#1f2937' : '#1f2937'};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: ${props => props.primary ? '#4de8a8' : '#f3f4f6'};
    border-color: ${props => props.primary ? '#5efba9' : '#d1d5db'};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  ${mobileCardStyles}
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.active ? '#065f46' : '#991b1b'};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 8px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &.edit {
    background: #5efba9;
    color: #1f2937;

    &:hover {
      background: #4de8a8;
    }
  }

  &.delete {
    background: #fee2e2;
    color: #991b1b;

    &:hover {
      background: #fecaca;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    padding: 10px 12px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px;
    width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: #5efba9;
    color: #1f2937;
    border: none;

    &:hover {
      background: #4de8a8;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

function AdminVoucherCodes() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [vouchers, setVouchers] = useState([]);
  const [adminBrandId, setAdminBrandId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    enabled: true,
    vtype: 'percent',
    amount: '',
    currency: 'OMR',
    percent_cap: '',
    min_subtotal: '0',
    max_total_uses: '',
    max_uses_per_user: '1',
    start_at: '',
    end_at: ''
  });
  const [error, setError] = useState(null);

  // Fetch admin's brand_id on mount
  useEffect(() => {
    const fetchAdminBrand = async () => {
      if (!session) return;

      try {
        // Get brand from brand_managers table
        const { data: brandManager, error: brandError } = await supabase
          .from('brand_managers')
          .select('brand_id')
          .eq('profile_id', session.user.id)
          .single();

        if (brandManager && !brandError) {
          console.log('Found brand from brand_managers:', brandManager.brand_id);
          setAdminBrandId(brandManager.brand_id);
        } else {
          console.log('No brand manager found for user, checking brands created_by...');
          
          // Fallback: Try to find brand by created_by (without .single() to handle empty results gracefully)
          const { data: userBrands, error: brandsError } = await supabase
            .from('brands')
            .select('id')
            .eq('created_by', session.user.id)
            .limit(1);
          
          if (brandsError) {
            console.error('Error fetching user brands:', brandsError);
            return;
          }
          
          if (userBrands && userBrands.length > 0) {
            console.log('Found brand by created_by:', userBrands[0].id);
            setAdminBrandId(userBrands[0].id);
          } else {
            console.log('No brand found for this user');
          }
        }
      } catch (error) {
        console.error('Error fetching admin brand:', error);
      }
    };

    fetchAdminBrand();
  }, [session]);

  // Fetch vouchers when brand is loaded
  useEffect(() => {
    if (adminBrandId) {
      fetchVouchers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminBrandId]);

  const fetchVouchers = async () => {
    if (!adminBrandId) return;

    try {
      setLoading(true);

      // Fetch vouchers that are scoped to this brand
      const { data: brandVouchers, error: vbError } = await supabase
        .from('voucher_brands')
        .select('voucher_id')
        .eq('brand_id', adminBrandId);

      if (vbError) throw vbError;

      const voucherIds = brandVouchers.map(vb => vb.voucher_id);

      if (voucherIds.length === 0) {
        setVouchers([]);
        setLoading(false);
        return;
      }

      // Fetch full voucher details
      const { data, error } = await supabase
        .from('voucher_codes')
        .select('*')
        .in('id', voucherIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVouchers(data || []);
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      alert('Failed to fetch voucher codes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!adminBrandId) {
      alert('Failed to get brand information');
      return;
    }

    try {
      setError(null);

      // Validate form
      if (!formData.code.trim()) {
        setError('Code is required');
        return;
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        setError('Amount must be greater than 0');
        return;
      }

      if (formData.vtype === 'percent' && parseFloat(formData.amount) > 100) {
        setError('Percentage cannot exceed 100%');
        return;
      }

      // Build payload
      const payload = {
        code: formData.code.trim().toUpperCase(),
        enabled: formData.enabled,
        vtype: formData.vtype,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        percent_cap: formData.percent_cap ? parseFloat(formData.percent_cap) : null,
        min_subtotal: parseFloat(formData.min_subtotal || 0),
        max_total_uses: formData.max_total_uses ? parseInt(formData.max_total_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user || 1),
        applies_to: 'brands', // Always brands for admin
        start_at: formData.start_at || null,
        end_at: formData.end_at || null
      };

      let savedVoucher;

      if (editingId) {
        // Update voucher
        const { data, error: updateError } = await supabase
          .from('voucher_codes')
          .update(payload)
          .eq('id', editingId)
          .select()
          .single();

        if (updateError) throw updateError;
        savedVoucher = data;
      } else {
        // Create voucher
        const { data, error: insertError } = await supabase
          .from('voucher_codes')
          .insert(payload)
          .select()
          .single();

        if (insertError) throw insertError;
        savedVoucher = data;
      }

      // Link voucher to admin's brand
      const { error: vbError } = await supabase
        .from('voucher_brands')
        .upsert([
          { voucher_id: savedVoucher.id, brand_id: adminBrandId }
        ]);

      if (vbError) throw vbError;

      setShowModal(false);
      setEditingId(null);
      resetForm();
      fetchVouchers();
    } catch (error) {
      console.error('Error saving voucher:', error);
      setError(error.message || 'Failed to save voucher code');
    }
  };

  const handleEdit = (voucher) => {
    setEditingId(voucher.id);
    setFormData({
      code: voucher.code,
      enabled: voucher.enabled,
      vtype: voucher.vtype,
      amount: voucher.amount.toString(),
      currency: voucher.currency,
      percent_cap: voucher.percent_cap ? voucher.percent_cap.toString() : '',
      min_subtotal: voucher.min_subtotal?.toString() || '0',
      max_total_uses: voucher.max_total_uses ? voucher.max_total_uses.toString() : '',
      max_uses_per_user: (voucher.max_uses_per_user || 1).toString(),
      start_at: voucher.start_at ? new Date(voucher.start_at).toISOString().split('T')[0] : '',
      end_at: voucher.end_at ? new Date(voucher.end_at).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this voucher code?')) return;

    try {
      const { error } = await supabase
        .from('voucher_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      alert('Failed to delete voucher code');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setError(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      code: '',
      enabled: true,
      vtype: 'percent',
      amount: '',
      currency: 'OMR',
      percent_cap: '',
      min_subtotal: '0',
      max_total_uses: '',
      max_uses_per_user: '1',
      start_at: '',
      end_at: ''
    });
  };

  const filteredVouchers = vouchers.filter(voucher =>
    voucher.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderVoucherMobileCard = (voucher) => (
    <MobileOrderCard key={voucher.id}>
      <MobileCardBody>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
          <MobileOrderHeaderMain>
            <MobileOrderId>{voucher.code}</MobileOrderId>
            <MobileOrderMeta>
              {voucher.amount}
              {voucher.vtype === 'percent' ? '%' : ` ${voucher.currency}`}
              {voucher.percent_cap && voucher.vtype === 'percent' && (
                <> ({isArabic ? 'الحد الأقصى' : 'max'} {voucher.percent_cap} {voucher.currency})</>
              )}
            </MobileOrderMeta>
            {voucher.min_subtotal > 0 && (
              <MobileOrderMeta>
                {isArabic ? 'الحد الأدنى' : 'Min'} {voucher.min_subtotal} {voucher.currency}
              </MobileOrderMeta>
            )}
            <MobileOrderMeta>
              {isArabic ? 'الاستخدام' : 'Usage'}: {voucher.total_uses || 0}
              {voucher.max_total_uses ? ` / ${voucher.max_total_uses}` : ''}
              {voucher.max_uses_per_user ? ` • ${voucher.max_uses_per_user} ${isArabic ? 'لكل مستخدم' : 'per user'}` : ''}
            </MobileOrderMeta>
            <MobileOrderMeta>
              {voucher.start_at && <div>{isArabic ? 'من:' : 'From:'} {new Date(voucher.start_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US')}</div>}
              {voucher.end_at && <div>{isArabic ? 'إلى:' : 'To:'} {new Date(voucher.end_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US')}</div>}
              {!voucher.start_at && !voucher.end_at && (isArabic ? 'بدون تاريخ انتهاء' : 'No expiry')}
            </MobileOrderMeta>
          </MobileOrderHeaderMain>
          <MobileOrderHeaderAside>
            <MobileBadgeRow>
              <StatusBadge active={voucher.enabled}>
                {voucher.enabled ? (isArabic ? 'مفعل' : 'Enabled') : (isArabic ? 'معطل' : 'Disabled')}
              </StatusBadge>
            </MobileBadgeRow>
          </MobileOrderHeaderAside>
        </div>
        <MobileCardActions>
          <ActionButton className="edit" onClick={() => handleEdit(voucher)}>
            <FiEdit2 /> {isArabic ? 'تعديل' : 'Edit'}
          </ActionButton>
          <ActionButton className="delete" onClick={() => handleDelete(voucher.id)}>
            <FiTrash2 /> {isArabic ? 'حذف' : 'Delete'}
          </ActionButton>
        </MobileCardActions>
      </MobileCardBody>
    </MobileOrderCard>
  );

  return (
    <Container>
      <Header>
        <Title>
          <FiTag /> {isArabic ? 'رموز الخصم' : 'Voucher Codes'}
        </Title>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder={isArabic ? 'بحث عن رموز الخصم...' : 'Search vouchers...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <IconButton onClick={() => setShowModal(true)} disabled={!adminBrandId} primary>
            <FiPlus /> {isArabic ? 'إضافة رمز خصم' : 'Add Voucher Code'}
          </IconButton>
          <IconButton onClick={fetchVouchers}>
            <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
          </IconButton>
        </HeaderActions>
      </Header>

      <Card>
        <DesktopOnly>
        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell>{isArabic ? 'الرمز' : 'Code'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الخصم' : 'Discount'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الاستخدام' : 'Usage'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'صالح' : 'Valid'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : filteredVouchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center', padding: '64px' }}>
                    <EmptyState>{isArabic ? 'لم يتم العثور على رموز خصم' : 'No voucher codes found'}</EmptyState>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVouchers.map(voucher => (
                  <TableRow key={voucher.id}>
                    <TableCell><strong>{voucher.code}</strong></TableCell>
                    <TableCell>
                      {voucher.amount}
                      {voucher.vtype === 'percent' ? '%' : ` ${voucher.currency}`}
                      {voucher.percent_cap && voucher.vtype === 'percent' && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          ({isArabic ? 'الحد الأقصى' : 'max'} {voucher.percent_cap} {voucher.currency})
                        </div>
                      )}
                      {voucher.min_subtotal > 0 && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {isArabic ? 'الحد الأدنى' : 'min'} {voucher.min_subtotal} {voucher.currency}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>{voucher.total_uses || 0}</div>
                      {voucher.max_total_uses && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          / {voucher.max_total_uses} {isArabic ? 'الحد الأقصى' : 'max'}
                        </div>
                      )}
                      {voucher.max_uses_per_user && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {voucher.max_uses_per_user} {isArabic ? 'لكل مستخدم' : 'per user'}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div style={{ fontSize: '12px' }}>
                        {voucher.start_at && <div>{isArabic ? 'من:' : 'From:'} {new Date(voucher.start_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US')}</div>}
                        {voucher.end_at && <div>{isArabic ? 'إلى:' : 'To:'} {new Date(voucher.end_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US')}</div>}
                        {!voucher.start_at && !voucher.end_at && '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={voucher.enabled}>
                        {voucher.enabled ? (isArabic ? 'مفعل' : 'Enabled') : (isArabic ? 'معطل' : 'Disabled')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ActionButton className="edit" onClick={() => handleEdit(voucher)}>
                        <FiEdit2 />
                      </ActionButton>
                      <ActionButton className="delete" onClick={() => handleDelete(voucher.id)}>
                        <FiTrash2 />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
        </DesktopOnly>

        <MobileOnly>
          {loading ? (
            <EmptyState>{isArabic ? 'جاري التحميل...' : 'Loading...'}</EmptyState>
          ) : filteredVouchers.length === 0 ? (
            <EmptyState>{isArabic ? 'لم يتم العثور على رموز خصم' : 'No voucher codes found'}</EmptyState>
          ) : (
            <MobileOrderList>
              {filteredVouchers.map(renderVoucherMobileCard)}
            </MobileOrderList>
          )}
        </MobileOnly>
      </Card>

      {showModal && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingId ? (isArabic ? 'تعديل رمز الخصم' : 'Edit Voucher Code') : (isArabic ? 'إضافة رمز خصم' : 'Add Voucher Code')}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              {error && (
                <div style={{ color: '#dc2626', fontSize: '14px', padding: '8px', background: '#fee2e2', borderRadius: '6px' }}>
                  {error}
                </div>
              )}

              <FormGroup>
                <Label>{isArabic ? 'الرمز *' : 'Code *'}</Label>
                <Input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                  placeholder={isArabic ? 'مثال: SAVE10' : 'e.g. SAVE10'}
                />
              </FormGroup>

              <CheckboxLabel>
                <Input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleInputChange}
                />
                {isArabic ? 'مفعل' : 'Enabled'}
              </CheckboxLabel>

              <FormRow>
                <FormGroup>
                  <Label>{isArabic ? 'نوع الخصم *' : 'Discount Type *'}</Label>
                  <Select
                    name="vtype"
                    value={formData.vtype}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="percent">{isArabic ? 'نسبة مئوية (%)' : 'Percentage (%)'}</option>
                    <option value="fixed">{isArabic ? 'مبلغ ثابت' : 'Fixed Amount'}</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>{isArabic ? 'المبلغ *' : 'Amount *'}</Label>
                  <Input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    max={formData.vtype === 'percent' ? '100' : undefined}
                    placeholder={formData.vtype === 'percent' ? '10' : '5'}
                  />
                </FormGroup>
              </FormRow>

              {formData.vtype === 'percent' && (
                <FormGroup>
                  <Label>{isArabic ? 'الحد الأقصى للخصم (اختياري)' : 'Maximum Discount Cap (Optional)'}</Label>
                  <Input
                    type="number"
                    name="percent_cap"
                    value={formData.percent_cap}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder={isArabic ? 'مثال: 50 ريال' : 'e.g. 50 OMR'}
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>{isArabic ? 'الحد الأدنى للإجمالي الفرعي' : 'Minimum Subtotal'}</Label>
                <Input
                  type="number"
                  name="min_subtotal"
                  value={formData.min_subtotal}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>{isArabic ? 'الحد الأقصى للاستخدام الإجمالي (0 = غير محدود)' : 'Max Total Uses (0 = unlimited)'}</Label>
                  <Input
                    type="number"
                    name="max_total_uses"
                    value={formData.max_total_uses}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{isArabic ? 'الحد الأقصى للاستخدام لكل مستخدم' : 'Max Uses Per User'}</Label>
                  <Input
                    type="number"
                    name="max_uses_per_user"
                    value={formData.max_uses_per_user}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="1"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>{isArabic ? 'صالح من (اختياري)' : 'Valid From (optional)'}</Label>
                  <Input
                    type="date"
                    name="start_at"
                    value={formData.start_at}
                    onChange={handleInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{isArabic ? 'صالح حتى (اختياري)' : 'Valid Until (optional)'}</Label>
                  <Input
                    type="date"
                    name="end_at"
                    value={formData.end_at}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormRow>

              <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
                <strong>{isArabic ? 'ملاحظة:' : 'Note:'}</strong> {isArabic ? 'سيتم تطبيق هذا الرمز فقط على المنتجات من علامتك التجارية.' : 'This voucher will apply only to products from your brand.'}
              </div>

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleCloseModal}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button type="submit" className="primary">
                  {editingId ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'إنشاء' : 'Create')}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default AdminVoucherCodes;
